import styled from "styled-components";
import { dashboardTheme } from "./theme";
import type { ThemeMode } from "../types";

type ThemeModeProps = {
    themeMode?: ThemeMode;
};

type ActiveProps = {
    active?: boolean;
};

type ColorProps = {
    color: string;
};

type LayoutProps = {
    $leftCollapsed?: boolean;
    $rightCollapsed?: boolean;
};

type PanelCollapseProps = {
    $collapsed?: boolean;
};

type PanelHeaderProps = {
    $framed?: boolean;
};

type PanelSideProps = {
    $side?: "left" | "right";
};

export const Surface = styled.div<ThemeModeProps>`
    ${dashboardTheme};
    color: var(--calendar-text);
    background: var(--calendar-bg);
    min-height: calc(100vh - 64px);
    padding: 0 0 2rem;
`;

export const StickyHeader = styled.div`
    padding: 0.55rem 1rem 0;
    background: var(--calendar-bg);
`;

export const HeaderBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    background: var(--calendar-shell);
    border: 1px solid var(--calendar-border);
    border-radius: var(--calendar-radius-shell);
    padding: 0.65rem 0.9rem;
    box-shadow: var(--calendar-shadow);
    flex-wrap: nowrap;

    @media (max-width: 1180px) {
        flex-wrap: wrap;
    }
`;

export const PageName = styled.div`
    font-weight: 800;
    font-size: 1.1rem;
    white-space: nowrap;
`;

export const HeaderTools = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: nowrap;
    flex: 1 1 auto;

    @media (max-width: 1180px) {
        flex-wrap: wrap;
    }
`;

export const SearchWrap = styled.div`
    position: relative;
    min-width: 260px;
    flex: 1 1 280px;
`;

export const SearchInput = styled.input`
    width: 100%;
    height: 42px;
    border-radius: var(--calendar-radius-input);
    border: 1px solid var(--calendar-border);
    background: var(--calendar-surface-secondary);
    color: var(--calendar-text);
    padding: 0 1rem;
    box-sizing: border-box;
    font-size: 13px;
    outline: none;
`;

export const SearchResults = styled.div`
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    background: var(--calendar-shell);
    border: 1px solid var(--calendar-border);
    border-radius: var(--calendar-radius-card);
    box-shadow: var(--calendar-shadow);
    overflow: hidden;
    z-index: 5;
`;

export const SearchResultButton = styled.button`
    width: 100%;
    border: none;
    background: transparent;
    color: var(--calendar-text);
    padding: 0.85rem 1rem;
    text-align: left;
    border-bottom: 1px solid var(--calendar-border);
    cursor: pointer;
`;

export const GhostSelect = styled.select`
    height: 42px;
    border-radius: var(--calendar-radius-control);
    border: 1px solid var(--calendar-border);
    background: var(--calendar-surface-secondary);
    color: var(--calendar-text);
    padding: 0 2.4rem 0 0.85rem;
    font-size: 12px;
    appearance: none;
`;

export const ToggleButton = styled.button`
    height: 42px;
    border-radius: var(--calendar-radius-control);
    border: 1px solid var(--calendar-border);
    background: var(--calendar-surface-secondary);
    color: var(--calendar-text);
    padding: 0 1rem;
    font-weight: 700;
    cursor: pointer;
`;

export const ThemeToggleButton = styled.button`
    height: 42px;
    width: 78px;
    border-radius: var(--calendar-radius-control);
    border: 1px solid var(--calendar-border);
    background: var(--calendar-surface-secondary);
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    overflow: visible;
`;

export const LocationPill = styled.button`
    height: 42px;
    min-width: 150px;
    border-radius: var(--calendar-radius-control);
    border: 1px solid var(--calendar-border);
    background: var(--calendar-surface-secondary);
    justify-content: flex-start;
    color: var(--calendar-text);
    padding: 0 0.9rem;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 12px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    cursor: pointer;

    &:hover {
        border-color: var(--calendar-border-strong);
        background: var(--calendar-surface-tertiary);
    }
`;

export const PrimaryAction = styled.button`
    height: 42px;
    border-radius: var(--calendar-radius-control);
    border: none;
    background: var(--calendar-accent);
    color: var(--calendar-bg);
    padding: 0 1.1rem;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
`;

export const GradientActionButton = styled.button<{ $elevated?: boolean }>`
    appearance: none;
    height: 42px;
    border: none;
    border-radius: var(--calendar-radius-control);
    background: linear-gradient(135deg, #2563eb 0%, #7c3aed 52%, #db2777 100%);
    color: #fff;
    padding: 0 1rem;
    box-sizing: border-box;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    cursor: pointer;
    box-shadow: ${p => p.$elevated ? "0 10px 22px rgba(124, 58, 237, 0.22)" : "0 6px 14px rgba(124, 58, 237, 0.16)"};

    &:focus-visible {
        outline: 2px solid var(--calendar-accent);
        outline-offset: 2px;
    }
`;

export const Layout = styled.div<LayoutProps>`
    display: grid;
    grid-template-columns: ${p => p.$leftCollapsed ? "52px" : "252px"} minmax(0, 1fr) ${p => p.$rightCollapsed ? "56px" : "320px"};
    gap: 1rem;
    padding: 1rem;
    align-items: stretch;
    transition: grid-template-columns 0.18s cubic-bezier(0.2, 0, 0, 1);

    @media (max-width: 1180px) {
        grid-template-columns: 1fr;
    }
`;

export const Panel = styled.div`
    background: var(--calendar-shell);
    border: 1px solid var(--calendar-border);
    border-radius: var(--calendar-radius-shell);
    box-shadow: var(--calendar-shadow);
    overflow: hidden;
`;

export const LeftPanel = styled(Panel)<PanelCollapseProps>`
    padding: ${p => p.$collapsed ? "0" : "0.9rem"};
    display: grid;
    grid-template-rows: ${p => p.$collapsed ? "1fr" : "none"};
    gap: ${p => p.$collapsed ? "0" : "1rem"};
    align-content: ${p => p.$collapsed ? "stretch" : "start"};
    align-self: start;
    min-height: 760px;
    transition: padding 0.18s cubic-bezier(0.2, 0, 0, 1);

    @media (max-width: 1180px) {
        min-height: auto;
    }
`;

export const CenterPanel = styled(Panel)`
    display: grid;
    grid-template-rows: auto auto 1fr;
    min-height: 760px;

    @media (max-width: 1180px) {
        min-height: auto;
    }
`;

export const RightPanel = styled(Panel)<PanelCollapseProps>`
    display: grid;
    grid-template-rows: ${p => p.$collapsed ? "1fr" : "auto auto 1fr"};
    min-height: 760px;

    @media (max-width: 1180px) {
        min-height: auto;
    }
`;

export const PanelHeaderRow = styled.div<PanelHeaderProps>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    min-width: 0;
    padding: ${p => p.$framed ? "0.85rem 1rem" : "0"};
    border-bottom: ${p => p.$framed ? "1px solid var(--calendar-border)" : "none"};
`;

export const PanelHeaderTitle = styled.div`
    color: var(--calendar-text);
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.1em;
    line-height: 1.2;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    text-transform: uppercase;
    white-space: nowrap;
`;

export const PanelCollapseButton = styled.button`
    width: 30px;
    height: 30px;
    border-radius: var(--calendar-radius-badge);
    border: 1px solid var(--calendar-border-strong);
    background: var(--calendar-surface-secondary);
    color: var(--calendar-text-soft);
    display: inline-grid;
    place-items: center;
    flex: 0 0 auto;
    font-size: 18px;
    line-height: 1;
    padding: 0;
    cursor: pointer;
    transition: border-color 0.16s ease, color 0.16s ease, background 0.16s ease;

    &:hover {
        border-color: var(--calendar-accent);
        color: var(--calendar-accent);
        background: var(--calendar-accent-soft);
    }
`;

export const CollapsedPanelButton = styled.button<PanelSideProps>`
    width: 100%;
    min-height: 100%;
    border: none;
    background: transparent;
    color: var(--calendar-text-soft);
    padding: ${p => p.$side ? "1.7rem 0 0.8rem" : "0.8rem 0"};
    display: grid;
    grid-template-rows: auto auto;
    align-content: start;
    align-items: center;
    justify-items: center;
    gap: 0.8rem;
    cursor: pointer;
    transition: background 0.16s ease, color 0.16s ease;

    &:hover {
        background: var(--calendar-surface-secondary);
        color: var(--calendar-accent);
    }

    @media (max-width: 1180px) {
        min-height: 54px;
        grid-template-columns: auto auto;
        grid-template-rows: 1fr;
        align-content: center;
        justify-content: center;
        padding: 0 0.85rem;
    }
`;

export const PanelCollapseGlyph = styled.span`
    width: 100%;
    height: 100%;
    display: inline-grid;
    place-items: center;
    font-family: Arial, sans-serif;
    font-size: 22px;
    font-weight: 800;
    line-height: 0;
    transform: translateY(-1px);
`;

export const CollapsedPanelLabel = styled.span<PanelSideProps>`
    writing-mode: vertical-rl;
    transform: ${p => p.$side === "left" ? "rotate(180deg)" : "none"};
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.1em;
    line-height: 1;
    text-transform: uppercase;

    @media (max-width: 1180px) {
        writing-mode: horizontal-tb;
        transform: none;
    }
`;

export const SectionLabel = styled.div`
    font-size: 10px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--calendar-section-label);
    font-weight: 700;
    min-width: 0;
    max-width: 100%;
    overflow-wrap: anywhere;
`;

export const SectionHeadingRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
`;

export const SectionHeadingActions = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    flex: 0 0 auto;
`;

export const SectionEditButton = styled.button`
    height: 24px;
    border-radius: var(--calendar-radius-badge);
    border: 1px solid var(--calendar-border-strong);
    background: transparent;
    color: var(--calendar-text-soft);
    padding: 0 0.55rem;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    cursor: pointer;

    &:hover {
        color: var(--calendar-accent);
        border-color: var(--calendar-accent);
    }
`;

export const SectionInfoButton = styled.button<{ active?: boolean }>`
    width: 24px;
    height: 24px;
    border-radius: var(--calendar-radius-badge);
    border: 1px solid ${p => p.active ? "var(--calendar-accent)" : "var(--calendar-border-strong)"};
    background: ${p => p.active ? "var(--calendar-accent-soft)" : "transparent"};
    color: ${p => p.active ? "var(--calendar-accent)" : "var(--calendar-text-soft)"};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    cursor: pointer;

    &:hover {
        color: var(--calendar-accent);
        border-color: var(--calendar-accent);
    }
`;

export const SectionInfoPopover = styled.div`
    position: fixed;
    z-index: 80;
    width: min(260px, calc(100vw - 32px));
    padding: 0.75rem 0.85rem;
    border: 1px solid var(--calendar-border-strong);
    border-radius: var(--calendar-radius-card);
    background: var(--calendar-shell);
    color: var(--calendar-text);
    box-shadow: 0 18px 40px rgba(15, 23, 42, 0.22);
    font-size: 12px;
    font-weight: 600;
    line-height: 1.45;
`;

export const FilterRow = styled.div`
    display: grid;
    gap: 0.6rem;
`;

export const FilterItem = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: none;
    background: transparent;
    color: var(--calendar-text);
    cursor: pointer;
    padding: 0;
    font-size: 13px;
`;

export const FilterLabel = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
`;

export const ConnectionCard = styled.div`
    background: var(--calendar-surface-secondary);
    border: 1px solid var(--calendar-border);
    border-radius: var(--calendar-radius-card);
    padding: 0.75rem 0.9rem;
`;

export const ConnectionRow = styled.button`
    width: 100%;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.9rem;
    border: none;
    background: transparent;
    color: var(--calendar-text);
    cursor: pointer;
    padding: 0;
    text-align: left;
`;

export const ConnectionLabel = styled.div`
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: center;
    gap: 0.55rem;
    min-width: 0;
`;

export const ConnectionText = styled.div`
    min-width: 0;
    text-align: left;
    display: grid;
    gap: 0.28rem;
`;

export const ConnectionTitle = styled.div`
    font-size: 13px;
    line-height: 1.45;
    font-weight: 700;
`;

export const ConnectionMeta = styled.div`
    font-size: 11px;
    color: var(--calendar-text-soft);
`;

export const ConnectionToggleWrap = styled.div`
    display: flex;
    justify-content: flex-end;
    padding-right: 0.15rem;
`;

export const ColorSwatch = styled.span<ColorProps>`
    width: 8px;
    height: 8px;
    border-radius: var(--calendar-radius-round);
    background: ${p => p.color};
    box-shadow: 0 0 0 3px rgba(255,255,255,0.02);
`;

export const Toggle = styled.button<ActiveProps>`
    appearance: none;
    -webkit-appearance: none;
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    flex: 0 0 auto;
    width: 34px;
    height: 20px;
    min-width: 34px;
    border-radius: var(--calendar-radius-toggle);
    border: none;
    padding: 0;
    background: ${p => p.active ? "var(--calendar-toggle-active-bg, #5b6ef5)" : "rgba(91, 110, 245, 0.28)"};
    background-color: ${p => p.active ? "var(--calendar-toggle-active-bg, #5b6ef5)" : "rgba(91, 110, 245, 0.28)"};
    position: relative;
    cursor: pointer;
    outline: none;

    ::after {
        content: "";
        position: absolute;
        top: 3px;
        left: ${p => p.active ? "17px" : "3px"};
        width: 14px;
        height: 14px;
        border-radius: var(--calendar-radius-toggle);
        background: ${p => p.active ? "var(--calendar-toggle-active-thumb, #fff)" : "#fff"};
        transition: left 0.18s ease;
    }
`;
